// Gemini API moved to server-side - no longer needed in client
import { supabase } from './supabase';
import { ActivityService } from './activity';
import { NotificationService } from './notifications';
import * as Sentry from '@sentry/react';

export interface AnalysisRequest {
  creativeUrl: string;
  fileName?: string;
  creativeType: 'image' | 'video';
  mimeType?: string;
  platforms?: string[];
  campaignType?: string;
  industry?: string;
  creativeId?: string;
  videoDuration?: number; // Duration in seconds for video files
}

export interface PlatformAnalysis {
  complianceScore: number;
  performanceScore: number;
  status: string;
  predictedCTR: number;
  predictedEngagement: number;
  algorithmFavorability: number;
  issues: Array<{
    severity: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    description: string;
    rule: string;
    recommendation: string;
    timestamp?: number;
    coordinates?: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
  }>;
  strengths: string[];
  weaknesses: string[];
}

export interface PerformanceAnalysis {
  overallPerformanceScore: number;
  brandAwarenessScore: number;
  keyFactors: {
    attention: {
      score: number;
      description: string;
      details: string[];
    };
    clarity: {
      score: number;
      description: string;
      details: string[];
    };
    engagement: {
      score: number;
      description: string;
      details: string[];
    };
    branding: {
      score: number;
      description: string;
      details: string[];
    };
  };
  attentionHeatmap: {
    hotspots: Array<{
      x: number;
      y: number;
      intensity: number;
      element: string;
    }>;
  };
}

export interface AnalysisResult {
  analysisId: string;
  timestamp: string;
  overallScore: number;
  analysisDate: string;
  fileInfo: {
    name: string;
    type: string;
    size: string;
  };
  platforms: Record<string, PlatformAnalysis>;
  performanceAnalysis: PerformanceAnalysis;
  aiRecommendations: Array<{
    category: string;
    priority: 'low' | 'medium' | 'high';
    title: string;
    description: string;
    impact: string;
    platforms: string[];
    timestamp?: number;
  }>;
  detectedIssues: Array<{
    id: number;
    platform: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    description: string;
    rule: string;
    ruleUrl: string;
    recommendation: string;
    timestamp?: number;
    duration?: number;
    coordinates?: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
  }>;
  // Video-specific fields
  videoDuration?: number;
  timelineAnalysis?: {
    keyMoments: Array<{
      timestamp: number;
      description: string;
      attentionLevel: number;
      complianceIssue: boolean;
    }>;
    engagementCurve: Array<{
      time: number;
      engagement: number;
    }>;
  };
  audioAnalysis?: {
    musicCompliance: boolean;
    voiceoverQuality: number;
    backgroundMusicVolume: string;
    soundEffectsCompliance: boolean;
    copyrightIssues: string[];
    audioVisualSync: number;
    audioClarity: number;
    platformSpecificAudio: {
      [platform: string]: {
        autoPlayOptimized?: boolean;
        copyrightCompliant: boolean;
        communityStandardsCompliant: boolean;
        contentIdSafe?: boolean;
        advertiserFriendly?: boolean;
        trendingAudioUsed?: boolean;
        originalAudio?: boolean;
      };
    };
    detectedAudioIssues: Array<{
      platform: string;
      severity: 'low' | 'medium' | 'high' | 'critical';
      issue: string;
      description: string;
      recommendation: string;
      timestamp?: number;
    }>;
  };
  // Image metadata for enhancement
  metadata?: {
    width: number;
    height: number;
    aspectRatio: number;
  };
}

export class AIAnalysisService {
  /**
   * Analyze creative using server-side API route
   */
  static async analyzeCreative(request: AnalysisRequest): Promise<AnalysisResult> {
    try {
      console.log('🚀 AIAnalysisService: Starting analysis via API route...');
      console.log('📊 Request:', { ...request, creativeUrl: request.creativeUrl.substring(0, 50) + '...' });

      // Get current session for authentication
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Authentication required. Please log in to analyze creatives.');
      }

      // Call the Vercel API route
      const response = await fetch('/api/analyze-creative', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle specific error cases with user-friendly messages
        if (response.status === 413) {
          throw new Error(errorData.details || errorData.message || 'File too large. Please use videos under 100MB and images under 50MB.');
        }
        
        if (response.status === 504 || response.status === 502) {
          throw new Error(errorData.details || errorData.message || 'Analysis timed out. Try with a smaller file or shorter video (under 2 minutes recommended).');
        }
        
        if (response.status === 429) {
          throw new Error(errorData.details || errorData.message || 'Service temporarily unavailable. Please try again in a minute.');
        }
        
        throw new Error(errorData.error || errorData.message || `API request failed with status ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Analysis failed');
      }

      console.log('✅ Analysis completed via API route');
      
      // Return both analysis and credit information for immediate UI updates
      return {
        analysis: result.analysis,
        analysesUsed: result.analysesUsed,
        analysesLimit: result.analysesLimit
      };

    } catch (error) {
      console.error('❌ Analysis error:', error);
      Sentry.captureException(error, {
        tags: { operation: 'analyze_creative', component: 'AIAnalysisService' },
        contexts: {
          analysis: {
            creativeType: request.creativeType,
            platforms: request.platforms,
            hasCreativeId: !!request.creativeId,
            videoDuration: request.videoDuration
          }
        }
      });
      throw error;
    }
  }

  /**
   * Get analysis history from Supabase
   */
  static async getAnalysisHistory(limit: number = 10): Promise<AnalysisResult[]> {
    try {
      const { data, error } = await supabase
        .from('analyses')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw error;
      }

      return data?.map(item => item.analysis_data as unknown as AnalysisResult) || [];
    } catch (error) {
      console.error('Error fetching analysis history:', error);
      Sentry.captureException(error, {
        tags: { operation: 'get_analysis_history', component: 'AIAnalysisService' },
        contexts: {
          analysisHistory: {
            limit
          }
        }
      });
      throw error;
    }
  }

  /**
   * Get specific analysis by ID with creative URL
   */
  static async getAnalysisById(analysisId: string): Promise<{analysis: AnalysisResult, creativeUrl: string, creativeId?: string, versionNumber?: number, enhancedImageUrl?: string} | null> {
    try {
      console.log('🔍 AIAnalysisService: Fetching analysis by ID:', analysisId);
      
      const { data, error } = await supabase
        .from('analyses')
        .select('analysis_data, creative_url, file_name, created_at, creative_id, enhanced_image_url')
        .eq('id', analysisId)
        .single();

      if (error) {
        // Handle specific error cases
        if (error.code === 'PGRST116') {
          console.log('⚠️ Analysis not found (406 error) - ID:', analysisId);
          return null;
        }
        throw error;
      }

      if (data?.analysis_data) {
        // Parse the JSON data to get a proper AnalysisResult object
        const analysis = data.analysis_data as unknown as AnalysisResult;
        
        // Ensure fileInfo exists
        if (!analysis.fileInfo) {
          analysis.fileInfo = {
            name: (data.file_name as string) || 'Creative',
            type: 'unknown',
            size: '0'
          };
        } else {
          // Update the file name in the analysis data with the current file_name from the database
          // This ensures that renamed analyses show the updated name
          if (data.file_name && typeof data.file_name === 'string') {
            analysis.fileInfo.name = data.file_name;
          }
        }
        
        // Get version information from creative if available
        let versionNumber: number | undefined;
        if (data.creative_id) {
          try {
            const { data: creativeData, error: creativeError } = await supabase
              .from('creatives')
              .select('version_number')
              .eq('id', data.creative_id)
              .single();
              
            if (!creativeError && creativeData?.version_number) {
              versionNumber = creativeData.version_number;
            }
          } catch (creativeError) {
            console.warn('Could not fetch creative version info:', creativeError);
            Sentry.captureException(creativeError, {
              tags: { operation: 'get_creative_version', component: 'AIAnalysisService' },
              level: 'warning',
              contexts: {
                creative: {
                  creativeId: data.creative_id
                }
              }
            });
          }
        }
        
        return {
          analysis: analysis,
          creativeUrl: data.creative_url, 
          creativeId: data.creative_id,
          versionNumber,
          enhancedImageUrl: data.enhanced_image_url
        };
      }
      
      console.log('⚠️ No analysis data found for ID:', analysisId);
      return null;
    } catch (error) {
      console.error('❌ Error fetching analysis by ID:', error);
      Sentry.captureException(error, {
        tags: { operation: 'get_analysis_by_id', component: 'AIAnalysisService' },
        contexts: {
          analysis: {
            analysisId
          }
        }
      });
      throw error;
    }
  }

  /**
   * Get analysis for a specific creative ID (team-aware)
   */
  static async getAnalysisForCreative(creativeId: string): Promise<AnalysisResult | null> {
    try {
      console.log('🔍 AIAnalysisService: Fetching analysis for creative:', creativeId);
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .rpc('get_team_aware_analysis_by_creative_id', {
          creative_id_param: creativeId,
          calling_user_id: user.id
        });

      if (error) {
        console.log('⚠️ No analysis found for creative ID:', creativeId);
        return null;
      }

      // Extract the single analysis from array
      const analysisData = data?.[0] || null;
      
      if (analysisData?.analysis_data) {
        // Parse the JSON data to get a proper AnalysisResult object
        const analysis = analysisData.analysis_data as unknown as AnalysisResult;
        
        // Ensure analysisId is set
        if (!analysis.analysisId && analysisData.id) {
          analysis.analysisId = analysisData.id;
        }
        
        // Ensure fileInfo exists
        if (!analysis.fileInfo) {
          analysis.fileInfo = {
            name: (analysisData.file_name as string) || 'Creative',
            type: 'unknown',
            size: '0'
          };
        } else {
          // Update the file name in the analysis data with the current file_name from the database
          if (analysisData.file_name && typeof analysisData.file_name === 'string') {
            analysis.fileInfo.name = analysisData.file_name;
          }
        }
        
        console.log('✅ Found analysis for creative:', creativeId);
        return analysis;
      }
      
      console.log('⚠️ No analysis data found for creative:', creativeId);
      return null;
    } catch (error) {
      console.error('❌ Error fetching analysis for creative:', error);
      Sentry.captureException(error, {
        tags: { operation: 'get_analysis_for_creative', component: 'AIAnalysisService' },
        contexts: {
          creative: {
            creativeId
          }
        }
      });
      throw error;
    }
  }

  /**
   * Fix missing creative_id relationships in analyses table
   */
  static async fixMissingCreativeIds(): Promise<void> {
    try {
      console.log('🔧 Fixing missing creative_id relationships...');
      
      // Get analyses without creative_id
      const { data: analysesWithoutCreativeId, error: fetchError } = await supabase
        .from('analyses')
        .select('id, creative_url, file_name')
        .is('creative_id', null);

      if (fetchError) {
        console.error('Error fetching analyses without creative_id:', fetchError);
        return;
      }

      if (!analysesWithoutCreativeId || analysesWithoutCreativeId.length === 0) {
        console.log('✅ No analyses with missing creative_id found');
        return;
      }

      console.log(`Found ${analysesWithoutCreativeId.length} analyses without creative_id`);

      // Fix each analysis
      for (const analysis of analysesWithoutCreativeId) {
        try {
          // Try to find the creative by URL
          const { data: creative, error: creativeError } = await supabase
            .from('creatives')
            .select('id')
            .eq('url', analysis.creative_url)
            .single();

          if (!creativeError && creative) {
            // Update the analysis with the creative_id
            const { error: updateError } = await supabase
              .from('analyses')
              .update({ creative_id: creative.id })
              .eq('id', analysis.id);

            if (updateError) {
              console.error(`Failed to update analysis ${analysis.id}:`, updateError);
            } else {
              console.log(`✅ Fixed creative_id for analysis ${analysis.id}`);
            }
          }
        } catch (err) {
          console.error(`Error processing analysis ${analysis.id}:`, err);
          Sentry.captureException(err, {
            tags: { operation: 'fix_creative_id', component: 'AIAnalysisService' },
            contexts: {
              analysis: {
                analysisId: analysis.id,
                creativeUrl: analysis.creative_url
              }
            }
          });
        }
      }

      console.log('✅ Finished fixing missing creative_id relationships');
    } catch (error) {
      console.error('❌ Error in fixMissingCreativeIds:', error);
      Sentry.captureException(error, {
        tags: { operation: 'fix_missing_creative_ids', component: 'AIAnalysisService' }
      });
    }
  }

  /**
   * Debug analysis relationships
   */
  static async debugAnalysisRelationships(): Promise<void> {
    try {
      console.log('🐛 Debugging analysis relationships...');
      
      // Get some analyses to check
      const { data: analyses, error: analysesError } = await supabase
        .from('analyses')
        .select('id, creative_id, creative_url, file_name')
        .limit(10)
        .order('created_at', { ascending: false });

      if (analysesError) {
        console.error('Error fetching analyses:', analysesError);
        return;
      }

      console.log('Recent analyses:', analyses?.map(a => ({
        id: a.id,
        creative_id: a.creative_id,
        has_creative_id: !!a.creative_id,
        url_preview: a.creative_url?.substring(0, 50) + '...'
      })));

      // Check for creatives without analyses
      const { data: creativesWithoutAnalysis, error: creativesError } = await supabase
        .from('creatives')
        .select('id, url, name')
        .limit(10)
        .order('created_at', { ascending: false });

      if (!creativesError && creativesWithoutAnalysis) {
        for (const creative of creativesWithoutAnalysis) {
          const { data: analysis } = await supabase
            .from('analyses')
            .select('id')
            .eq('creative_id', creative.id)
            .single();

          console.log(`Creative ${creative.id} (${creative.name}): ${analysis ? 'Has analysis' : 'NO ANALYSIS'}`);
        }
      }
    } catch (error) {
      console.error('❌ Error in debugAnalysisRelationships:', error);
      Sentry.captureException(error, {
        tags: { operation: 'debug_analysis_relationships', component: 'AIAnalysisService' }
      });
    }
  }
}